/* 0.47.96 - The public site under a path of another website.
 *
 * GT18 normally runs at the root of its own address (caf.govtech.am).
 * It can also be reached under a path of another site, for example
 * https://eipo.am/gt18: that site's web server forwards /gt18/... to
 * the GT18 server, and GT18 serves its start page with the path
 * written into it (window.__GT18__.base, see backend
 * routers/site_index.py). Without it (the root, or a server that
 * serves the start page directly) BASE is '' and nothing here changes
 * anything.
 *
 * With a base, everything the app asks for or links to with an
 * address that starts with "/" gets the base in front:
 *   - fetch() and XMLHttpRequest (API calls, /version.json, media)
 *   - links, pictures, video, frames and forms in the page (also the
 *     ones written in content: Markdown links, library values), kept
 *     right by a watcher on the document
 *   - page navigation through React Router (its basename)
 * and appPath() gives the page's own path without the base, for code
 * that reads window.location.pathname.
 */

function normalize(b) {
  const s = String(b || '').trim();
  if (!s || s === '/') return '';
  const t = ('/' + s.replace(/^\/+/, '')).replace(/\/+$/, '');
  return /^\/[A-Za-z0-9._~\-/]+$/.test(t) ? t : '';
}

const cfg = (typeof window !== 'undefined' && window.__GT18__) || {};

/** '' at the root, '/gt18' under a path. */
export const BASE = normalize(cfg.base);

/** The site's own settings for this visit, written by the server. */
export const SITE_CONFIG = cfg;

const needsBase = (u) => (
  typeof u === 'string' && BASE !== ''
  && u.charAt(0) === '/' && u.charAt(1) !== '/'
  && u !== BASE && !u.startsWith(BASE + '/') && !u.startsWith(BASE + '?') && !u.startsWith(BASE + '#')
);

/** An address of this site with the base in front, when it needs one. */
export function withBase(u) {
  return needsBase(u) ? BASE + u : u;
}

/** The page's path without the base: "/gt18/en/p/x" -> "/en/p/x". */
export function appPath(pathname) {
  const p = pathname == null
    ? (typeof window !== 'undefined' ? window.location.pathname : '/')
    : String(pathname);
  if (!BASE) return p || '/';
  if (p === BASE) return '/';
  return p.startsWith(BASE + '/') ? p.slice(BASE.length) : p;
}

const ATTRS = {
  A: ['href'], AREA: ['href'], IMG: ['src', 'srcset'], SOURCE: ['src', 'srcset'],
  VIDEO: ['src', 'poster'], AUDIO: ['src'], IFRAME: ['src'], EMBED: ['src'],
  OBJECT: ['data'], FORM: ['action'], LINK: ['href'], TRACK: ['src'],
};

function fixSrcset(v) {
  return v.split(',').map(part => {
    const t = part.trim();
    const sp = t.search(/\s/);
    const url = sp < 0 ? t : t.slice(0, sp);
    return needsBase(url) ? BASE + url + (sp < 0 ? '' : t.slice(sp)) : t;
  }).join(', ');
}

function fixElement(el) {
  const names = ATTRS[el.tagName];
  if (!names) return;
  for (const n of names) {
    const v = el.getAttribute(n);
    if (!v) continue;
    if (n === 'srcset') {
      const f = fixSrcset(v);
      if (f !== v) el.setAttribute(n, f);
    } else if (needsBase(v)) {
      el.setAttribute(n, BASE + v);
    }
  }
}

function fixTree(root) {
  if (!root || root.nodeType !== 1) return;
  fixElement(root);
  const sel = Object.keys(ATTRS).join(',');
  root.querySelectorAll(sel).forEach(fixElement);
}

let installed = false;

export function installBasePath() {
  if (installed || !BASE || typeof window === 'undefined') return;
  installed = true;

  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    if (typeof input === 'string') return origFetch(withBase(input), init);
    if (typeof Request !== 'undefined' && input instanceof Request) {
      const u = new URL(input.url);
      if (u.origin === window.location.origin && needsBase(u.pathname)) {
        return origFetch(new Request(BASE + u.pathname + u.search + u.hash, input), init);
      }
    }
    return origFetch(input, init);
  };

  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    return origOpen.call(this, method, typeof url === 'string' ? withBase(url) : url, ...rest);
  };

  const start = () => {
    fixTree(document.documentElement);
    new MutationObserver((list) => {
      for (const m of list) {
        if (m.type === 'attributes') fixElement(m.target);
        else m.addedNodes.forEach(fixTree);
      }
    }).observe(document.documentElement, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['href', 'src', 'srcset', 'poster', 'data', 'action'],
    });
  };
  if (document.documentElement) start();
  else document.addEventListener('DOMContentLoaded', start);
}

installBasePath();
